"use client";
import { Form } from "@/app/components/shadcn/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState, useEffect } from "react";
import {
  LoginPhoneData,
  loginPhoneSchema,
  LoginVerificationData,
  loginVerificationSchema,
} from "@/app/schemas/loginSchema";
import useAuth from "@/app/lib/features/auth/auth.hook";
import { trackLogin } from "@/app/lib/tracking/events";
import { syncConsentFromServer } from "@/app/lib/tracking/consent";
import { trackGeneric } from "@/app/lib/tracking/interactions";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import { clearHomeData } from "@/app/lib/features/home/home.slice";
import { clearError } from "@/app/lib/features/auth/auth.slice";
import { useRouter, useSearchParams } from "next/navigation";
import LoginPhoneForm from "./login/LoginPhoneForm";
import VerificationForm from "./shared/VerificationForm";
import { toast } from "sonner";

const LoginForm = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [showVerification, setShowVerification] = useState(false);

  const {
    sendLoginCode,
    verifyLoginCode,
    isLoading,
    authError,
  } = useAuth();

  // Unmount (sayfa değişimi) ve step değişiminde hata temizle
  useEffect(() => {
    dispatch(clearError());
  }, [showVerification, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const phoneForm = useForm<LoginPhoneData>({
    resolver: zodResolver(loginPhoneSchema),
    defaultValues: {
      phone: "",
      countryId: 212,
    },
  });

  const verificationForm = useForm<LoginVerificationData>({
    resolver: zodResolver(loginVerificationSchema),
    defaultValues: {
      code: "",
    },
  });

  const onPhoneSubmit = useCallback(
    async (data: LoginPhoneData) => {
      setPhoneNumber(data.phone);
      const success = await sendLoginCode({
        phone: data.phone,
      });

      if (success) {
        toast.success("Doğrulama kodu telefonunuza gönderildi.");
        setShowVerification(true);
      } else {
        toast.error(authError || "Kod gönderilemedi. Lütfen tekrar deneyin.");
      }
    },
    [sendLoginCode, authError],
  );

  const onVerificationSubmit = useCallback(
    async (data: LoginVerificationData) => {
      if (isVerifying || !phoneNumber) return;

      setIsVerifying(true);

      const success = await verifyLoginCode({
        phone: phoneNumber,
        code: parseInt(data.code, 10),
      });

      if (success) {
        trackLogin("phone");
        trackGeneric("login");
        syncConsentFromServer();
        toast.success("Giriş başarılı! Yönlendiriliyorsunuz...");
        dispatch(clearHomeData());
        const redirect = searchParams.get("redirect");
        router.replace(redirect || "/");
        setIsVerifying(false);
      } else {
        toast.error(authError || "Geçersiz kod. Lütfen tekrar deneyin.");
        setIsVerifying(false);
      }
    },
    [phoneNumber, verifyLoginCode, isVerifying, authError, dispatch, searchParams, router],
  );

  const handleBackToPhone = useCallback(() => {
    setShowVerification(false);
    setPhoneNumber("");
    setIsVerifying(false);
    verificationForm.reset();
  }, [verificationForm]);

  const handleResendCode = useCallback(async () => {
    if (phoneNumber) {
      const success = await sendLoginCode({
        phone: phoneNumber,
      });

      if (success) {
        toast.success("Doğrulama kodu yeniden gönderildi.");
      } else {
        toast.error("Kod gönderilemedi. Lütfen tekrar deneyin.");
      }
    }
  }, [phoneNumber, sendLoginCode]);

  if (showVerification) {
    return (
      <Form {...verificationForm}>
        <form onSubmit={verificationForm.handleSubmit(onVerificationSubmit)}>
          <VerificationForm
            form={verificationForm}
            phoneNumber={phoneNumber}
            submitButtonText="Giriş Yap"
            isLoading={isVerifying}
            error={authError}
            onBack={handleBackToPhone}
            onResend={handleResendCode}
            onErrorClear={() => dispatch(clearError())}
          />
        </form>
      </Form>
    );
  }

  return (
    <Form {...phoneForm}>
      <form
        onSubmit={phoneForm.handleSubmit(onPhoneSubmit)}
        className="space-y-6"
      >
        <LoginPhoneForm
          form={phoneForm}
          isLoading={isLoading}
          error={authError}
          onErrorClear={() => dispatch(clearError())}
        />
      </form>
    </Form>
  );
};

export default LoginForm;